import React, { useContext, useEffect, useState } from 'react';
import { formatDttm } from '../../../Util/timezone';

import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
  Button,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import InfoIcon from '@material-ui/icons/Info';

import { AlertContext } from '../../../Context/AlertContextProvider';
import { getMergedProductDetails } from '../../../Actions';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    height: '100%',
    boxSizing: 'border-box',
    transition: 'border 0.15s ease',
  },
  cardSelected: {
    border: '2px solid #2e7d32',
  },
  cardUnselected: {
    border: '2px solid transparent',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: '1px solid #e0e0e0',
  },
  mfgName: {
    fontWeight: 700,
    fontSize: 17,
    lineHeight: 1.2,
  },
  lastModified: {
    fontSize: 11,
    color: '#888',
    marginTop: 3,
  },
  newerBadge: {
    display: 'inline-block',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontSize: 10,
    fontWeight: 700,
    padding: '1px 7px',
    borderRadius: 10,
    marginLeft: 8,
    verticalAlign: 'middle',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  printedBadge: {
    display: 'inline-block',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontSize: 10,
    fontWeight: 700,
    padding: '1px 7px',
    borderRadius: 10,
    marginLeft: 8,
    verticalAlign: 'middle',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  roleBadgeKeeper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
  },
  roleBadgeMerged: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#fff3e0',
    color: '#e65100',
    border: '1px solid #ffcc80',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
  },
  keepButton: {
    backgroundColor: '#FF682C',
    color: '#fff',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
    '&:hover': { backgroundColor: '#e55520' },
  },
  keepButtonSelected: {
    backgroundColor: '#2e7d32',
    color: '#fff',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
    '&:hover': { backgroundColor: '#1b5e20' },
  },
  fieldsSection: {
    marginBottom: theme.spacing(2),
  },
  fieldRow: {
    display: 'flex',
    padding: '5px 6px',
    borderRadius: 3,
    marginBottom: 2,
  },
  fieldRowDiff: {
    backgroundColor: '#fff8e1',
    borderLeft: '3px solid #f57c00',
    paddingLeft: 5,
  },
  fieldLabel: {
    color: '#888',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    minWidth: 130,
    flexShrink: 0,
    paddingTop: 1,
  },
  fieldValue: {
    fontSize: 13,
    color: '#222',
    wordBreak: 'break-word',
  },
  fieldValueEmpty: {
    fontSize: 13,
    color: '#bbb',
    fontStyle: 'italic',
  },
  skuSection: {
    marginTop: theme.spacing(1.5),
  },
  skuHeader: {
    fontWeight: 600,
    fontSize: 13,
    marginBottom: theme.spacing(0.5),
    color: '#444',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  skuCount: {
    display: 'inline-block',
    backgroundColor: '#ececec',
    borderRadius: 10,
    padding: '0 7px',
    fontSize: 11,
    color: '#555',
    fontWeight: 700,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
    '& th': { fontWeight: 700, fontSize: 11, color: '#555', padding: '6px 10px' },
  },
  tableCell: {
    fontSize: 12,
    padding: '5px 10px',
    height: 36,
  },
  skuMatchRow: {
    backgroundColor: '#e8f5e9',
  },
  skuPlaceholderRow: {
    backgroundColor: '#fafafa',
  },
  discontinued: {
    color: '#c62828',
    fontSize: 11,
    fontWeight: 600,
  },
  scoreBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5),
    padding: '6px 10px',
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #e0e0e0',
  },
  scoreChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 12,
    fontWeight: 600,
  },
  scoreHigh:   { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  scoreMid:    { backgroundColor: '#fff8e1', color: '#f57c00' },
  scoreLow:    { backgroundColor: '#fce4ec', color: '#c62828' },
  scoreLabel:  { fontWeight: 400, color: '#666', fontSize: 11 },
  diffLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: '#666',
    marginBottom: theme.spacing(1.5),
    padding: '6px 10px',
    backgroundColor: '#fffde7',
    borderRadius: 4,
    border: '1px solid #ffe082',
  },
  diffDot: {
    width: 10,
    height: 10,
    backgroundColor: '#f57c00',
    borderRadius: 2,
    flexShrink: 0,
  },
  diffWord: {
    backgroundColor: '#ffcc02',
    borderRadius: 2,
    padding: '0 2px',
  },
  metaToggle: {
    marginTop: theme.spacing(1.5),
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#888',
    fontSize: 11,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '&:hover': { color: '#555' },
  },
  metaSection: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
    border: '1px solid #eee',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: 4,
    gap: 8,
    '&:last-child': { marginBottom: 0 },
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#999',
    textTransform: 'uppercase',
    minWidth: 80,
    flexShrink: 0,
  },
  metaValue: {
    fontSize: 11,
    color: '#444',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  metaLink: {
    fontSize: 11,
    color: '#1565c0',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
}));

function buildSkuRows(skusA, skusB) {
  const norm = (s) => (s || '').trim().toUpperCase();
  const mapA = new Map();
  const mapB = new Map();
  for (const s of skusA) { const k = norm(s.product_sku); if (k) mapA.set(k, s); }
  for (const s of skusB) { const k = norm(s.product_sku); if (k) mapB.set(k, s); }
  const keys = new Set([...mapA.keys(), ...mapB.keys()]);
  return [...keys].map((key) => ({
    key,
    skuA: mapA.get(key) || null,
    skuB: mapB.get(key) || null,
    matched: mapA.has(key) && mapB.has(key),
  })).sort((a, b) => {
    if (a.matched !== b.matched) return a.matched ? -1 : 1;
    return a.key.localeCompare(b.key);
  });
}

function buildDiffTokens(ownValue, otherValue) {
  if (!ownValue || !otherValue) return null;
  const otherWords = new Set(otherValue.toLowerCase().split(/\s+/));
  return ownValue.split(/(\s+)/).map((token) => ({
    text: token,
    isDiff: !/^\s+$/.test(token) && !otherWords.has(token.toLowerCase()),
  }));
}

const PRODUCT_FIELDS = [
  { key: 'style',               label: 'Style Number',        alwaysShow: true },
  { key: 'trade_name',          label: 'Style Name',          alwaysShow: true },
  { key: 'material_class',      label: 'Material Class',      alwaysShow: true },
  { key: 'material_sub_class',  label: 'Material Sub Class',  alwaysShow: false },
  { key: 'size_cd',             label: 'Size',                alwaysShow: false },
  { key: 'backing_cd',          label: 'Backing',             alwaysShow: false },
  { key: 'collection',          label: 'Collection',          alwaysShow: false },
  { key: 'private_style_number',label: 'Private Style #',     alwaysShow: false },
  { key: 'private_style_name',  label: 'Private Style Name',  alwaysShow: false },
  { key: 'display_name',        label: 'Display Name',        alwaysShow: false },
];

function ProductCard({ product, skuRows, loadingSkus, side, isKeeper, onKeep, diffFields, fieldDiffTokens, isNewer, mode, cardRole }) {
  const classes = useStyles();
  const [showMeta, setShowMeta] = useState(false);

  const visibleFields = PRODUCT_FIELDS.filter(
    ({ key, alwaysShow }) =>
      alwaysShow ||
      diffFields.has(key) ||
      (product[key] != null && product[key] !== '')
  );

  const headerAction = mode === 'unmerge' ? (
    cardRole === 'keeper'
      ? <span className={classes.roleBadgeKeeper}><CheckCircleOutlineIcon style={{ fontSize: 13 }} /> Kept</span>
      : <span className={classes.roleBadgeMerged}>Was Merged</span>
  ) : (
    <Button
      variant="contained"
      size="small"
      className={isKeeper ? classes.keepButtonSelected : classes.keepButton}
      onClick={onKeep}
      startIcon={isKeeper ? <CheckCircleOutlineIcon /> : null}
    >
      {isKeeper ? 'Keeping' : 'Keep This One'}
    </Button>
  );

  return (
    <Paper
      elevation={isKeeper || cardRole === 'keeper' ? 4 : 1}
      className={`${classes.card} ${(isKeeper || cardRole === 'keeper') ? classes.cardSelected : classes.cardUnselected}`}
    >
      {/* Header */}
      <Box className={classes.cardHeader}>
        <Box style={{ minWidth: 0 }}>
          <Typography className={classes.mfgName}>
            {product.manufacturer_name}
            {isNewer && <span className={classes.newerBadge}>Most Recent</span>}
            {product.has_been_printed && <span className={classes.printedBadge}>Printed QR</span>}
          </Typography>
          <Typography className={classes.lastModified}>
            Last modified: {formatDttm(product.last_modified_dttm, { fallback: 'Unknown' })}
          </Typography>
        </Box>
        {headerAction}
      </Box>

      {/* Product fields */}
      <Box className={classes.fieldsSection}>
        {visibleFields.map(({ key, label }) => {
          const isDiff = diffFields.has(key);
          const value = product[key];
          const tokens = isDiff ? fieldDiffTokens[key] : null;
          return (
            <Box
              key={key}
              className={`${classes.fieldRow} ${isDiff ? classes.fieldRowDiff : ''}`}
            >
              <Typography className={classes.fieldLabel}>{label}</Typography>
              {value != null && value !== '' ? (
                tokens ? (
                  <span className={classes.fieldValue}>
                    {tokens.map((t, i) =>
                      t.isDiff
                        ? <span key={i} className={classes.diffWord}>{t.text}</span>
                        : <span key={i}>{t.text}</span>
                    )}
                  </span>
                ) : (
                  <Typography className={classes.fieldValue}>{value}</Typography>
                )
              ) : (
                <Typography className={classes.fieldValueEmpty}>—</Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* SKUs — aligned with the other card via shared skuRows order */}
      <Box className={classes.skuSection}>
        <Typography className={classes.skuHeader}>
          SKUs{' '}
          <span className={classes.skuCount}>
            {loadingSkus ? '…' : skuRows.filter((r) => (side === 'a' ? r.skuA : r.skuB)).length}
          </span>
          {!loadingSkus && skuRows.some((r) => r.matched) && (
            <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 400 }}>
              · {skuRows.filter((r) => r.matched).length} matching
            </span>
          )}
        </Typography>
        {loadingSkus ? (
          <Box textAlign="center" py={2}><CircularProgress size={24} /></Box>
        ) : skuRows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No SKUs found</Typography>
        ) : (
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Retail</TableCell>
                <TableCell>UOM</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {skuRows.map(({ key, skuA, skuB, matched }) => {
                const sku = side === 'a' ? skuA : skuB;
                const isPlaceholder = !sku;
                return (
                  <TableRow
                    key={key}
                    className={matched ? classes.skuMatchRow : isPlaceholder ? classes.skuPlaceholderRow : ''}
                  >
                    <TableCell className={classes.tableCell} style={{ fontFamily: 'monospace', color: isPlaceholder ? '#ccc' : 'inherit' }}>
                      {key}
                      {sku?.discontinued === 1 && (
                        <Tooltip title="Discontinued">
                          <InfoIcon fontSize="inherit" color="secondary" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ color: isPlaceholder ? '#ccc' : 'inherit' }}>
                      {sku ? (sku.color_name || sku.color || '—') : '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ color: isPlaceholder ? '#ccc' : 'inherit' }}>
                      {sku ? (Number(sku.cost) === 0 ? 'Call' : `$${sku.cost}`) : '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ color: isPlaceholder ? '#ccc' : 'inherit' }}>
                      {sku ? (Number(sku.retail_price) === 0 ? 'Call' : `$${sku.retail_price}`) : '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ color: isPlaceholder ? '#ccc' : 'inherit' }}>
                      {sku ? (sku.uom || '—') : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      <button className={classes.metaToggle} onClick={() => setShowMeta((v) => !v)}>
        {showMeta ? '▴' : '▾'} Technical details
      </button>
      {showMeta && (
        <Box className={classes.metaSection}>
          <Box className={classes.metaRow}>
            <span className={classes.metaLabel}>UUID</span>
            <span className={classes.metaValue}>{product.product_uuid}</span>
          </Box>
          {product.data_source_cd && (
            <Box className={classes.metaRow}>
              <span className={classes.metaLabel}>Source</span>
              <span className={classes.metaValue}>{product.data_source_cd}</span>
            </Box>
          )}
          <Box className={classes.metaRow}>
            <span className={classes.metaLabel}>Page</span>
            <a
              className={classes.metaLink}
              href={`/product/${product.product_uuid}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open product page ↗
            </a>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

const DuplicateMergeReview = ({ productA, productB, keeperUuid, onKeep, mode = 'merge', scores = null }) => {
  const classes = useStyles();
  const { handleAlertOpen, setMessage, setMessageType } = useContext(AlertContext);

  const [skusA, setSkusA] = useState([]);
  const [skusB, setSkusB] = useState([]);
  const [loadingA, setLoadingA] = useState(true);
  const [loadingB, setLoadingB] = useState(true);

  useEffect(() => {
    getMergedProductDetails(productA.product_uuid)
      .then((res) => setSkusA(res.data.data?.[0]?.options || []))
      .catch(() => {
        setMessage('Error loading SKU details for Product A');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setLoadingA(false));

    getMergedProductDetails(productB.product_uuid)
      .then((res) => setSkusB(res.data.data?.[0]?.options || []))
      .catch(() => {
        setMessage('Error loading SKU details for Product B');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setLoadingB(false));
  }, [productA.product_uuid, productB.product_uuid]);

  // Fields that differ between the two products
  const diffFields = new Set(
    PRODUCT_FIELDS
      .filter(({ key }) => (productA[key] || '') !== (productB[key] || ''))
      .map(({ key }) => key)
  );

  // Word-level diff tokens for each differing field
  const diffTokensA = {};
  const diffTokensB = {};
  diffFields.forEach((key) => {
    diffTokensA[key] = buildDiffTokens(productA[key], productB[key]);
    diffTokensB[key] = buildDiffTokens(productB[key], productA[key]);
  });

  const skuRows = buildSkuRows(skusA, skusB);
  const loadingSkus = loadingA || loadingB;

  // Which product was modified more recently
  const aDate = new Date(productA.last_modified_dttm || 0);
  const bDate = new Date(productB.last_modified_dttm || 0);
  const newerIs = aDate > bDate ? 'a' : bDate > aDate ? 'b' : null;

  // For unmerge mode: identify which product was kept vs merged
  // The loser (merged) will have merged_product_uuid set
  const cardRoleA = mode === 'unmerge'
    ? (productA.merged_product_uuid ? 'merged' : 'keeper')
    : null;
  const cardRoleB = mode === 'unmerge'
    ? (productB.merged_product_uuid ? 'merged' : 'keeper')
    : null;

  const scoreChipClass = (v) => {
    if (v == null) return classes.scoreChip;
    return `${classes.scoreChip} ${v >= 0.97 ? classes.scoreHigh : v >= 0.90 ? classes.scoreMid : classes.scoreLow}`;
  };
  const pct = (v) => v != null ? `${(Number(v) * 100).toFixed(1)}%` : '—';

  return (
    <Box>
      {scores && (
        <Box className={classes.scoreBar}>
          {[
            { label: 'Overall',  val: scores.composite },
            { label: 'Style #',  val: scores.style     },
            { label: 'Name',     val: scores.name      },
            { label: 'Mfg',     val: scores.mfg       },
            { label: 'Size',     val: scores.size      },
          ].map(({ label, val }) => (
            <span key={label} className={scoreChipClass(val)}>
              <span className={classes.scoreLabel}>{label}</span>
              {pct(val)}
            </span>
          ))}
        </Box>
      )}
      {diffFields.size > 0 && (
        <Box className={classes.diffLegend}>
          <span className={classes.diffDot} />
          Highlighted fields differ between these two products
        </Box>
      )}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <ProductCard
            product={productA}
            skuRows={skuRows}
            loadingSkus={loadingSkus}
            side="a"
            isKeeper={keeperUuid === productA.product_uuid}
            onKeep={() => onKeep(productA.product_uuid)}
            diffFields={diffFields}
            fieldDiffTokens={diffTokensA}
            isNewer={newerIs === 'a'}
            mode={mode}
            cardRole={cardRoleA}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProductCard
            product={productB}
            skuRows={skuRows}
            loadingSkus={loadingSkus}
            side="b"
            isKeeper={keeperUuid === productB.product_uuid}
            onKeep={() => onKeep(productB.product_uuid)}
            diffFields={diffFields}
            fieldDiffTokens={diffTokensB}
            isNewer={newerIs === 'b'}
            mode={mode}
            cardRole={cardRoleB}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DuplicateMergeReview;
